// packages/frontend/src/projects/ESID-25-011/pages/simulationlibrary/index.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Checkbox,
  Button,
  IconButton,
  Chip,
  Grid,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  Upload as UploadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import ErrorIcon from '@mui/icons-material/Error';
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { tokens } from '../../../../theme';
import SimulationSettingsModal from "../../components/simulationSettingsModal";
import useApi from "../../../shared/hooks/useApi";
import { useSimulation } from "../../context/SimulationContext";
import ReadOnlySchedulerTable from "../../components/ReadOnlySchedulerTable";

export default function SimulationLibrary({ setLoading, triggerAlert }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { apiGet, apiPost } = useApi();

  // Get simulations from context
  const { simulations, runningSimulations, pendingSimulations, isLoading: isLoadingSimulations, error: simulationsError, fetchSimulations ,allSimulationsStatus} = useSimulation();

  const [selectedSimulations, setSelectedSimulations] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [simulateModalOpen, setSimulateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0]);//30 days ago
  const [endDate, setEndDate] = useState(new Date(new Date().setDate(new Date().getDate())).toISOString().split('T')[0]);//today
  const [expandedRows, setExpandedRows] = useState([]);
  const [simulationSettings, setSimulationSettings] = useState({});
  const [loadingSettings, setLoadingSettings] = useState({});
  const [expandedConfigs, setExpandedConfigs] = useState([]);

  // Use simulations from context
  const allSimulations = simulations || [];
  const allRunningSimulations = runningSimulations || [];
  const allPendingSimulations = pendingSimulations || [];
  const statusSimulations = allSimulationsStatus || [];
  
  // Merge all simulations, with pending and status simulations at the top
  const mergedSimulations = [...allPendingSimulations, ...statusSimulations, ...allRunningSimulations, ...allSimulations];

 

  // Update loading state when isLoadingSimulations changes
  useEffect(() => {
    setLoading(isLoadingSimulations);
  }, [isLoadingSimulations, setLoading]);

  // call API to get all simulations status


  // Filter simulations based on search and filters
  const filteredSimulations = mergedSimulations.filter((simulation) => {
    // Extract name and status from message if available, otherwise use simulation properties
    let simulationName = simulation.name || "";
    let simulationStatus = simulation.status || "";

    if (simulation.message) {
      try {
        // Handle NaN values in JSON before parsing
        let cleanMessage = simulation.message.replace(/:\s*NaN\s*([,}])/g, ': null$1');
        const messageData = JSON.parse(cleanMessage);
        if (messageData && typeof messageData === 'object') {
          simulationName = messageData.name || simulation.name || "";
          simulationStatus = messageData.status || simulation.status || "";
        }
      } catch (error) {
        console.error("Error parsing simulation message:", error);
        // Keep the original values if parsing fails
      }
    }

    const matchesSearch = (simulationName?.toLowerCase() ?? "").includes(searchTerm.toLowerCase());

    // Date filtering
    let matchesDateRange = true;
    if (simulation.created && simulation.created !== "LAST") {
      const simDate = new Date(simulation.created);
      const simDateStr = simDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD

      if (startDate && simDateStr < startDate) {
        matchesDateRange = false;
      }
      if (endDate && simDateStr > endDate) {
        matchesDateRange = false;
      }
    }

    return matchesSearch && matchesDateRange;
  });


  const handleSelectSimulation = (simulationId) => {
    // Find the simulation to check if it's running
    const simulation = allSimulations.find(sim => sim.event_id === simulationId);
    const isRunningSimulation = simulation && (!simulation.partition_key || simulation.partition_key.includes('RUNNING'));

    if (isRunningSimulation) {
      setSnackbarMessage("Running simulations cannot be selected for deletion.");
      setSnackbarOpen(true);
      return;
    }

    setSelectedSimulations((prev) =>
      prev.includes(simulationId)
        ? prev.filter((id) => id !== simulationId)
        : [...prev, simulationId]
    );
  };

  const handleSelectAll = () => {
    // Only select completed simulations (not running ones)
    const completedSimulationIds = filteredSimulations
      .filter(sim => sim.partition_key && !sim.partition_key.includes('RUNNING'))
      .map((sim) => sim.event_id);
    setSelectedSimulations(completedSimulationIds);
  };

  const handleClearSelection = () => {
    setSelectedSimulations([]);
  };

  const confirmDelete = async () => {
    setLoading(true);
    setDeleteDialogOpen(false);
    await handleDelete();
    setLoading(false);
  };

  const handleStartNewSimulation = () => {
    setSimulateModalOpen(true);
  };


  const getStatusColor = (status) => {
    if (!status) return theme.palette.grey[500];
    let statusLower = status.toLowerCase();
    switch (statusLower) {
      case "running":
      case "pending":
      case "started":
      case "calculating":
      case "data-fetched":
      case "in progress":
        return "#FF9800"; // Orange for active
      case "completed":
      case "calculated":
        return "#4CAF50"; // Green for completed
      case "failed":
        return "#C62828"; // Red for failed
      case "error":
        return "#C62828"; // Red for error        
      default:
        return theme.palette.grey[500];
    }
  };

  const getStatusIcon = (status) => {
    if (!status) return <ScheduleIcon />;
    let statusLower = status.toLowerCase();
    switch (statusLower) {
      case "running":
      case "pending":
      case "started":
      case "calculating":
      case "data-fetched":
      case "in progress":
        return <PlayIcon />;
      case "completed":
      case "calculated":
        return <CheckCircleIcon />;
      case "error":
        return <ErrorIcon />;
      case "failed":
        return <ErrorIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return "Unknown";
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "in progress":
        return "In Progress";
      case "running":
        return "Running";
      case "pending":
        return "Pending";
      case "started":
        return "Started";
      case "calculating":
        return "Calculating";
      case "data-fetched":
        return "Data Fetched";
      case "completed":
        return "Completed";
      case "calculated":
        return "Calculated";
      case "error":
        return "Error";
      case "failed":
        return "Failed";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };


  // Fetch settings data when a row is expanded
  const fetchSimulationSettings = async (eventId) => {
    if (simulationSettings[eventId]) {
      // Already fetched
      return;
    }

    setLoadingSettings(prev => ({ ...prev, [eventId]: true }));

    try {
      // Extract s3_key from simulation
      const selectedSimulation = allSimulations.find(sim => sim.event_id === eventId);
      let s3_key = null;
      if (selectedSimulation && selectedSimulation.message) {
        try {
          // Remove BOM and other invisible characters
          let cleanMessage = selectedSimulation.message.trim().replace(/^\uFEFF/, '').replace(/^\u200B/, '');
          // Handle NaN values in JSON before parsing
          cleanMessage = cleanMessage.replace(/:\s*NaN\s*([,}])/g, ': null$1');
          let messageData = JSON.parse(cleanMessage);
          if (messageData && typeof messageData === 'object') {
            s3_key = messageData.s3_key;
          }
        } catch (error) {
          console.error("Error parsing simulation message:", error);
        }
      }

      // Build query params as object
      const queryParams = {
        simulation_id: eventId
      };
      if (s3_key) {
        queryParams.s3_key = s3_key;
      }

      const response = await apiGet("UX_HTTP_API", "/esid-25-006/get-simulation-settings-parquet", queryParams);

      console.log("Settings API Response:", response);

      // Handle the response - apiGet already parses JSON, so response is an object
      let parsedResponse = response;
      
      // If response is a string (shouldn't happen with apiGet, but handle just in case)
      if (typeof response === 'string') {
        let cleanResponse = response.replace(/:\s*NaN\s*([,}])/g, ': null$1');
        parsedResponse = JSON.parse(cleanResponse);
      }
      
      if (parsedResponse && parsedResponse.simulation && parsedResponse.simulation.settings) {
        // Extract the settings object from the response
        let settingsData = parsedResponse.simulation.settings;
        console.log("Extracted settings data:", settingsData);

        // Additional sanitization to handle any remaining NaN values
        settingsData = JSON.parse(JSON.stringify(settingsData, (key, value) => {
          if (typeof value === 'number' && isNaN(value)) {
            return null; // Convert NaN to null
          }
          return value;
        }));
        // remove key "no BESS" if it exists
        if (settingsData && Object.keys(settingsData).includes("no BESS")) {
          delete settingsData["no BESS"];
        }

        setSimulationSettings(prev => ({ ...prev, [eventId]: settingsData }));
      } else {
        setSimulationSettings(prev => ({ ...prev, [eventId]: null }));
      }
    } catch (error) {
      console.error("Error fetching simulation settings:", error);
      setSnackbarMessage(`Error loading settings data: ${error.message}`);
      setSnackbarOpen(true);
      setSimulationSettings(prev => ({ ...prev, [eventId]: null }));
    } finally {
      setLoadingSettings(prev => ({ ...prev, [eventId]: false }));
    }
  };

  // Handle row expansion
  const handleExpandRow = (eventId) => {
    const isExpanding = !expandedRows.includes(eventId);

    setExpandedRows(prev =>
      prev.includes(eventId)
        ? prev.filter(rowId => rowId !== eventId)
        : [...prev, eventId]
    );

    // Fetch settings data when expanding
    if (isExpanding) {
      fetchSimulationSettings(eventId);
    }
  };

  // Handle configuration expansion
  const handleExpandConfig = (eventId, configName) => {
    const configKey = `${eventId}-${configName}`;
    setExpandedConfigs(prev =>
      prev.includes(configKey)
        ? prev.filter(key => key !== configKey)
        : [...prev, configKey]
    );
  };

  const handleCloseSettings = () => {
    setSimulateModalOpen(false);
    fetchSimulations();
  };
  const handleSaveSettings = () => {
  };

  const handleDelete = async () => {
    // Find the corresponding simulation objects to get partition_key values
    // Only allow deletion of completed simulations (not running ones)
    const simulationsToDelete = allSimulations.filter(sim =>
      selectedSimulations.includes(sim.event_id) &&
      sim.partition_key &&
      !sim.partition_key.includes('RUNNING')
    );

    // Check if any running simulations were selected
    const runningSimulationsSelected = selectedSimulations.filter(simId => {
      const sim = allSimulations.find(s => s.event_id === simId);
      return sim && (!sim.partition_key || sim.partition_key.includes('RUNNING'));
    });

    if (runningSimulationsSelected.length > 0) {
      setSnackbarMessage(`Cannot delete ${runningSimulationsSelected.length} running simulation(s). Only completed simulations can be deleted.`);
      setSnackbarOpen(true);
      return;
    }

    if (simulationsToDelete.length === 0) {
      setSnackbarMessage("No completed simulations selected for deletion.");
      setSnackbarOpen(true);
      return;
    }

    // Extract partition_key and event_id pairs
    const deleteData = simulationsToDelete.map(sim => ({
      partition_key: sim.partition_key,
      event_id: sim.event_id
    }));

    const response = await apiPost("UX_HTTP_API", "/esid-25-006/delete-simulation", {
      simulations: deleteData
    });

    // Check if response contains success message or deleted_count
    if (response && (response.message?.includes("deleted successfully") || response.deleted_count > 0)) {
      setSelectedSimulations([]);
      setSnackbarMessage(response.message || "Simulations deleted successfully");
      setSnackbarOpen(true);
      // Refresh the simulation list
      await fetchSimulations();
    } else {
      setSnackbarMessage(response?.message || "Failed to delete simulations");
      setSnackbarOpen(true);
    }
  };
  return (
    <Box sx={{ padding: 4, marginLeft: 5, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="700" color="#2c3e50">
            Simulation History
          </Typography>


          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleStartNewSimulation}
            sx={{
              background: `linear-gradient(135deg, ${colors.entras_purple[300]} 0%, ${colors.entras_purple[300]} 100%)`,
              color: 'white',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: '600',
              px: 3,
              py: 1,
              boxShadow: `0 4px 12px ${colors.entras_purple[100]}40`,
              '&:hover': {
                background: `linear-gradient(135deg, ${colors.entras_purple[100]} 0%, ${colors.entras_purple[100]} 100%)`,
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 16px ${colors.entras_purple[100]}60`,
              },
            }}
          >
            Start New Simulation
          </Button>
        </Box>

        {/* Filters and Search */}
        <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search simulations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              }
            }}
          />
        </Box>
      </Box>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={(() => {
                      const completedSimulations = filteredSimulations.filter(sim => sim.partition_key && !sim.partition_key.includes('RUNNING'));
                      return selectedSimulations.length === completedSimulations.length && completedSimulations.length > 0;
                    })()}
                    indeterminate={(() => {
                      const completedSimulations = filteredSimulations.filter(sim => sim.partition_key && !sim.partition_key.includes('RUNNING'));
                      return selectedSimulations.length > 0 && selectedSimulations.length < completedSimulations.length;
                    })()}
                    onChange={(() => {
                      const completedSimulations = filteredSimulations.filter(sim => sim.partition_key && !sim.partition_key.includes('RUNNING'));
                      return selectedSimulations.length === completedSimulations.length ? handleClearSelection : handleSelectAll;
                    })()}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Details</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Simulation</TableCell>
                {/* <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Category</TableCell> */}
                <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Date Processed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSimulations.map((simulation) => (
                <React.Fragment key={simulation.event_id}>
                  <TableRow
                    hover
                    sx={{
                      '&:hover': { backgroundColor: '#f8f9fa' },
                      cursor: (() => {
                        const isRunningSimulation = !simulation.partition_key || simulation.partition_key.includes('RUNNING');
                        return isRunningSimulation ? 'default' : 'pointer';
                      })()
                    }}
                    onClick={() => {
                      const isRunningSimulation = !simulation.partition_key || simulation.partition_key.includes('RUNNING');
                      if (!isRunningSimulation) {
                        handleSelectSimulation(simulation.event_id);
                      }
                    }}
                  >
                    <TableCell padding="checkbox">
                      {(() => {
                        const isRunningSimulation = !simulation.partition_key || simulation.partition_key.includes('RUNNING');
                        return (
                          <Checkbox
                            checked={selectedSimulations.includes(simulation.event_id)}
                            onChange={() => handleSelectSimulation(simulation.event_id)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={isRunningSimulation}
                            sx={{
                              '&.Mui-disabled': {
                                color: '#ccc',
                                opacity: 0.6
                              }
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // Check if this is a running simulation (no partition_key or different partition_key)
                        const isRunningSimulation = !simulation.partition_key || simulation.partition_key.includes('RUNNING');

                        return (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExpandRow(simulation.event_id);
                            }}
                            disabled={isRunningSimulation}
                          >
                            {expandedRows.includes(simulation.event_id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="600" color="#2c3e50">
                          {(() => {
                            if (simulation.message) {
                              try {
                                // Handle NaN values in JSON before parsing
                                let cleanMessage = simulation.message.replace(/:\s*NaN\s*([,}])/g, ': null$1');
                                const messageData = JSON.parse(cleanMessage);
                                if (messageData && typeof messageData === 'object') {
                                  return messageData.name || simulation.name || "Unnamed Simulation";
                                }
                              } catch (error) {
                                // Fall through to return default name
                              }
                            }
                            return simulation.name || "Unnamed Simulation";
                          })()}
                        </Typography>
                        <Typography variant="caption" color="#7f8c8d" fontSize="0.75rem">
                          Event ID: {simulation.event_id}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={(() => {
                          let status = simulation.status || "";
                          if (simulation.message) {
                            try {
                              // Handle NaN values in JSON before parsing
                              let cleanMessage = simulation.message.replace(/:\s*NaN\s*([,}])/g, ': null$1');
                              const messageData = JSON.parse(cleanMessage);
                              if (messageData && typeof messageData === 'object') {
                                status = messageData.status || simulation.status || "";
                              }
                            } catch (error) {
                              // Use original status if parsing fails
                            }
                          }
                          return getStatusIcon(status);
                        })()}
                        label={(() => {
                          let status = simulation.status || "";
                          if (simulation.message) {
                            try {
                              // Handle NaN values in JSON before parsing
                              let cleanMessage = simulation.message.replace(/:\s*NaN\s*([,}])/g, ': null$1');
                              const messageData = JSON.parse(cleanMessage);
                              if (messageData && typeof messageData === 'object') {
                                status = messageData.status || simulation.status || "";
                                status = status?.toLowerCase();
                              }
                            } catch (error) {
                              // Use original status if parsing fails
                            }
                          }
                          return getStatusLabel(status);
                        })()}
                        size="small"
                        sx={{
                          backgroundColor: (() => {
                            let status = simulation.status || "";
                            if (simulation.message) {
                              try {
                                // Handle NaN values in JSON before parsing
                                let cleanMessage = simulation.message.replace(/:\s*NaN\s*([,}])/g, ': null$1');
                                const messageData = JSON.parse(cleanMessage);
                                if (messageData && typeof messageData === 'object') {
                                  status = messageData.status || simulation.status || "";
                                }
                              } catch (error) {
                                // Use original status if parsing fails
                              }
                            }
                            return getStatusColor(status);
                          })(),
                          color: "white",
                          fontWeight: "500",
                          fontSize: "0.75rem",
                          padding: "4px",
                          width: "100px"
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#2c3e50">
                        {simulation.created && simulation.created !== "LAST" ? (() => {
                          const date = new Date(simulation.created);
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const hours = String(date.getHours()).padStart(2, '0');
                          const minutes = String(date.getMinutes()).padStart(2, '0');
                          return `${year}-${month}-${day} ${hours}:${minutes}`;
                        })() : simulation.created === "LAST" ? "Latest" : 'N/A'}
                      </Typography>
                    </TableCell>

                  </TableRow>

                  {/* Expandable row for Settings data - only for completed simulations */}
                  {(() => {
                    // Check if this is a running simulation
                    const isRunningSimulation = !simulation.partition_key || simulation.partition_key.includes('RUNNING');

                    if (isRunningSimulation) {
                      return null; // Don't render expandable row for running simulations
                    }

                    return (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ padding: 0, border: 0 }}>
                          <Collapse in={expandedRows.includes(simulation.event_id)} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, backgroundColor: '#f8f9fa', borderRadius: 2, p: 3 }}>
                              {loadingSettings[simulation.event_id] || (!simulationSettings[simulation.event_id] && simulationSettings[simulation.event_id] !== null) ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 4, gap: 2 }}>
                                  <CircularProgress />
                                  <Typography variant="body1" color="#7f8c8d">
                                    Loading settings data...
                                  </Typography>
                                </Box>
                              ) : simulationSettings[simulation.event_id] && Object.keys(simulationSettings[simulation.event_id]).length > 0 ? (
                                <>
                                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600, mb: 0 }}>
                                      Simulation Settings
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      <Chip
                                        icon={<DescriptionIcon />}
                                        label={`${Object.keys(simulationSettings[simulation.event_id]).length} Configurations`}
                                        size="small"
                                        sx={{ backgroundColor: colors.entras_purple[300], color: 'white', fontWeight: 600 }}
                                      />
                                    </Box>
                                  </Box>

                                  {/* Display Settings data organized by configuration */}
                                  {(() => {
                                    // Only show the first configuration since all settings are the same
                                    const entries = Object.entries(simulationSettings[simulation.event_id] || {});
                                    console.log("Settings entries:", entries);
                                    if (entries.length === 0) return null;

                                    const [config, periods] = entries[0];
                                    console.log("Selected config:", config);
                                    console.log("Periods:", periods);
                                    const configKey = `${simulation.event_id}-${config}`;
                                    const isConfigExpanded = expandedConfigs.includes(configKey);
                                    const aggregatedData = periods.aggregated || periods[Object.keys(periods)[0]] || {};
                                    console.log("Aggregated data:", aggregatedData);

                                    // Group settings by category (extracted from flattened keys)
                                    const groupedSettings = {};
                                    Object.entries(aggregatedData).forEach(([key, value]) => {
                                      // Determine category from key prefix
                                      // Keys are now flattened: category_setting_name
                                      // Examples: pv_configuration_pv1_radio_button, electricity_consumption_consumption_other_radio_button
                                      let category = 'Other';
                                      let displayKey = key;

                                      if (key.startsWith('simulation_config_')) {
                                        category = 'Simulation Config';
                                        displayKey = key.substring('simulation_config_'.length);
                                      } else if (key.startsWith('pv_configuration_')) {
                                        category = 'PV Configuration';
                                        displayKey = key.substring('pv_configuration_'.length);
                                      } else if (key.startsWith('grid_connection_')) {
                                        category = 'Grid Connection';
                                        displayKey = key.substring('grid_connection_'.length);
                                      } else if (key.startsWith('electricity_consumption_')) {
                                        category = 'Electricity Consumption';
                                        displayKey = key.substring('electricity_consumption_'.length);
                                      } else if (key.startsWith('electricity_market_')) {
                                        category = 'Electricity Market';
                                        displayKey = key.substring('electricity_market_'.length);
                                      } else if (key.startsWith('battery_energy_storage_system_')) {
                                        category = 'Battery Energy Storage System';
                                        displayKey = key.substring('battery_energy_storage_system_'.length);
                                      } else if (key.startsWith('financial_')) {
                                        category = 'Financial';
                                        displayKey = key.substring('financial_'.length);
                                      }

                                      // For simulation_config dates, ensure timezone-aware ISO formatting for display
                                      if (
                                        key === 'simulation_config_simulation_from_date_iso' ||
                                        key === 'simulation_config_simulation_to_date_iso'
                                      ) {
                                        if (
                                          typeof value === 'string' &&
                                          value &&
                                          !value.includes('Z') &&
                                          !value.includes('+')
                                        ) {
                                          const parsed = dayjs(value);
                                          if (parsed.isValid()) {
                                            value = parsed.format('YYYY-MM-DDTHH:mm:ssZ');
                                          }
                                        }
                                      }

                                      if (!groupedSettings[category]) {
                                        groupedSettings[category] = [];
                                      }
                                      groupedSettings[category].push({ key: displayKey, fullKey: key, value });
                                    });

                                    console.log("Grouped settings:", groupedSettings);
                                    console.log("Number of categories:", Object.keys(groupedSettings).length);

                                    return (
                                      <Box key={config} sx={{ mb: 3 }}>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            backgroundColor: colors.entras_purple[300] + '10',
                                            p: 2,
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: colors.entras_purple[300] + '20' }
                                          }}
                                          onClick={() => handleExpandConfig(simulation.event_id, config)}
                                        >
                                          <IconButton size="small">
                                            {isConfigExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                          </IconButton>
                                          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.entras_purple[300], ml: 1 }}>
                                            Settings
                                          </Typography>
                                          <Chip
                                            label={`${Object.keys(groupedSettings).length} categories`}
                                            size="small"
                                            sx={{ ml: 2, backgroundColor: colors.entras_purple[300], color: 'white' }}
                                          />
                                        </Box>

                                        <Collapse in={isConfigExpanded} timeout="auto" unmountOnExit>
                                          <Box sx={{ mt: 2, pl: 4 }}>
                                            {Object.entries(groupedSettings).sort(([a], [b]) => a.localeCompare(b)).map(([category, settings]) => {
                                              // Check if this is Electricity Consumption category with scheduler fields
                                              const schedulerFields = settings.filter(({ key }) =>
                                                key === 'consumption_ev_fixed_charging_hours_unitless' ||
                                                key === 'consumption_ev_flex_charging_hours_unitless'
                                              );
                                              const normalFields = settings.filter(({ key }) =>
                                                key !== 'consumption_ev_fixed_charging_hours_unitless' &&
                                                key !== 'consumption_ev_flex_charging_hours_unitless'
                                              );
                                              const hasSchedulerFields = schedulerFields.length > 0;

                                              return (
                                                <Box key={category} sx={{ mb: 3 }}>
                                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1, pb: 1, borderBottom: `2px solid ${colors.entras_purple[300]}` }}>
                                                    {category}
                                                  </Typography>

                                                  {hasSchedulerFields ? (
                                                    // Special layout for categories with scheduler fields
                                                    <Grid container spacing={2}>
                                                      {/* Scheduler fields on the left */}
                                                      <Grid item xs={12} md={6}>
                                                        <Grid container spacing={2}>
                                                          {schedulerFields.map(({ key, fullKey, value }) => (
                                                            <Grid item xs={12} key={fullKey || key}>
                                                              <Box sx={{
                                                                width: '100%',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                backgroundColor: 'white',
                                                                borderRadius: 1,
                                                                border: '1px solid #e2e8f0',
                                                                p: 1.5
                                                              }}>
                                                                <ReadOnlySchedulerTable
                                                                  value={value}
                                                                  label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                />
                                                              </Box>
                                                            </Grid>
                                                          ))}
                                                        </Grid>
                                                      </Grid>

                                                      {/* Normal fields on the right in 2 columns */}
                                                      <Grid item xs={12} md={6}>
                                                        <Grid container spacing={2}>
                                                          {normalFields.map(({ key, fullKey, value }) => (
                                                            <Grid item xs={12} sm={6} key={fullKey || key}>
                                                              <Box sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                                                <Typography variant="caption" color="#7f8c8d" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                                                                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', wordBreak: 'break-word' }}>
                                                                  {Array.isArray(value)
                                                                    ? `[${value.length} items]`
                                                                    : typeof value === 'boolean'
                                                                      ? (value ? 'Yes' : 'No')
                                                                      : typeof value === 'number'
                                                                        ? (isNaN(value) ? 'N/A' : value.toLocaleString())
                                                                        : value === null
                                                                          ? 'N/A'
                                                                          : value ?? '-'}
                                                                </Typography>
                                                              </Box>
                                                            </Grid>
                                                          ))}
                                                        </Grid>
                                                      </Grid>
                                                    </Grid>
                                                  ) : (
                                                    // Normal layout for other categories
                                                    <Grid container spacing={2}>
                                                      {settings.map(({ key, fullKey, value }) => (
                                                        <Grid item xs={12} sm={6} md={4} key={fullKey || key}>
                                                          <Box sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                                            <Typography variant="caption" color="#7f8c8d" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                                                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', wordBreak: 'break-word' }}>
                                                              {Array.isArray(value)
                                                                ? `[${value.length} items]`
                                                                : typeof value === 'boolean'
                                                                  ? (value ? 'Yes' : 'No')
                                                                  : typeof value === 'number'
                                                                    ? (isNaN(value) ? 'N/A' : value.toLocaleString())
                                                                    : value === null
                                                                      ? 'N/A'
                                                                      : value ?? '-'}
                                                            </Typography>
                                                          </Box>
                                                        </Grid>
                                                      ))}
                                                    </Grid>
                                                  )}
                                                </Box>
                                              );
                                            })}

                                            {/* Show period-specific data if available */}
                                            {Object.keys(periods).length > 1 && (
                                              <Box sx={{ mt: 3 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7f8c8d', mb: 1 }}>
                                                  Available Periods: {Object.keys(periods).filter(p => p !== 'aggregated').join(', ')}
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        </Collapse>
                                      </Box>
                                    );
                                  })()}
                                </>
                              ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                                  <Typography variant="body1" color="#7f8c8d">
                                    No settings data available for this simulation.
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {selectedSimulations.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            zIndex: 1000
          }}
        >
          <Typography variant="body2" fontWeight="500" color="#2c3e50">
            {selectedSimulations.length} simulation{selectedSimulations.length > 1 ? 's' : ''} selected
          </Typography>
          <Button
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            variant="contained"
            color="error"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </Box>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight="600">
            Delete Simulations?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete {selectedSimulations.length}{" "}
            selected simulation{selectedSimulations.length > 1 ? "s" : ""}?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. All simulation data will be
            permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "500",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "500",
            }}
          >
            Delete Simulations
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SimulationSettingsModal
        open={simulateModalOpen}

        onClose={handleCloseSettings}
        setLoading={setLoading}
        onSave={handleSaveSettings}
      />
    </Box>
  );
}
